import React, { useState, useEffect } from 'react';
import {
  ShieldCheck, Play, Loader2, CheckCircle, XCircle, AlertCircle,
  ChevronDown, ChevronRight, Award
} from 'lucide-react';
import { conformanceApi } from '../../services/iso20022Api';

const MSG_BADGE = 'bg-gray-100 text-gray-700';

export default function Conformance() {
  const [suite, setSuite]       = useState(null);   // { categories, total, cases }
  const [report, setReport]     = useState(null);   // scored ConformanceReport
  const [running, setRunning]   = useState(false);
  const [loadErr, setLoadErr]   = useState(null);
  const [expanded, setExpanded] = useState({});      // caseId -> bool

  useEffect(() => {
    conformanceApi.getSuite()
      .then(setSuite)
      .catch(e => setLoadErr(e.response?.data?.message || e.message));
  }, []);

  const runAll = async () => {
    setRunning(true); setReport(null);
    try {
      const r = await conformanceApi.run();
      setReport(r);
    } catch (e) {
      setLoadErr(e.response?.data?.message || e.message);
    } finally {
      setRunning(false);
    }
  };

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  // Merge run results onto suite cases by id (so rows render before & after a run).
  const resultById = {};
  (report?.results || []).forEach(r => { resultById[r.id] = r; });

  const categories = suite?.categories || [];
  const casesByCat = (cat) => (suite?.cases || []).filter(c => c.category === cat);

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-primary-600" />
            Self-Test
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            A quick, unscored dry run across every message family: correct messages must
            validate, broken ones must be rejected — and each valid message is <span className="font-medium text-gray-700">signed
            with your active certificate</span> and verified against the corridor's signature profile,
            so a green run proves your own signing setup works. Nothing here affects your
            certification — use it freely while you build. When you're ready to be scored, go to{' '}
            <span className="font-medium text-gray-700">Certification</span>.
          </p>
        </div>
        <button onClick={runAll} disabled={running || !suite}
          className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-50">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? 'Running…' : 'Run certification'}
        </button>
      </div>

      {loadErr && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{loadErr}</p>
        </div>
      )}

      {/* Score summary (after a run) */}
      {report && (
        <div className={`rounded-xl border p-5 ${report.conformant ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center gap-4">
            {report.conformant
              ? <Award className="w-12 h-12 text-green-600 shrink-0" />
              : <AlertCircle className="w-12 h-12 text-amber-600 shrink-0" />}
            <div className="flex-1">
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-bold text-gray-900">{report.passed}/{report.total}</span>
                <span className="text-lg text-gray-600">{report.scorePct}%</span>
                <span className={`text-sm font-medium px-2.5 py-1 rounded-md ${report.conformant ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                  {report.conformant ? 'Conformant' : `${report.failed} failing`}
                </span>
              </div>
              {/* signing status */}
              {report.signingChecked ? (
                <p className="mt-2 text-xs text-green-800 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Every valid message was signed with your certificate and verified against the signature profile
                  {report.certificateSubject && (
                    <span className="font-mono text-green-700">· {report.certificateSubject}</span>
                  )}
                </p>
              ) : report.signingNote && (
                <p className="mt-2 text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded px-2 py-1">
                  {report.signingNote}
                </p>
              )}
              {/* per-category mini bars */}
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.values(report.byCategory || {}).map(cs => (
                  <div key={cs.category} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-36 truncate">{cs.category}</span>
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className={`h-full ${cs.passed === cs.total ? 'bg-green-500' : 'bg-amber-500'}`}
                           style={{ width: `${(cs.passed / cs.total) * 100}%` }} />
                    </div>
                    <span className="text-xs text-gray-500 w-10 text-right">{cs.passed}/{cs.total}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Suite, grouped by category */}
      {!suite && !loadErr && (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading suite…
        </div>
      )}

      {categories.map(cat => (
        <div key={cat}>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">{cat}</h2>
          <div className="space-y-2">
            {casesByCat(cat).map(c => {
              const res = resultById[c.id];
              const open = expanded[c.id];
              return (
                <div key={c.id} className="bg-white border border-gray-200 rounded-lg">
                  <button onClick={() => toggle(c.id)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 rounded-lg">
                    {/* status icon */}
                    {res
                      ? (res.passed ? <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                                    : <XCircle className="w-5 h-5 text-red-500 shrink-0" />)
                      : <div className="w-5 h-5 rounded-full border-2 border-gray-200 shrink-0" />}
                    <span className="font-mono text-xs text-gray-400 w-14 shrink-0">{c.id}</span>
                    <span className="flex-1 text-sm text-gray-900">{c.title}</span>
                    {res?.signed === true && (
                      <span className="flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-green-50 text-green-700">
                        <ShieldCheck className="w-3 h-3" /> signed
                      </span>
                    )}
                    {res?.signed === false && (
                      <span className="flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-red-50 text-red-700">
                        <ShieldCheck className="w-3 h-3" /> signature failed
                      </span>
                    )}
                    <span className={`font-mono text-xs px-2 py-0.5 rounded ${MSG_BADGE}`}>{c.messageType}</span>
                    {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  </button>

                  {open && (
                    <div className="px-4 pb-3 pl-12 space-y-2">
                      <p className="text-xs text-gray-500">{c.description}</p>
                      {/* assertions: from result if run, else from suite definition */}
                      <div className="space-y-1">
                        {(res?.assertions || c.assertions || []).map((a, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            {res
                              ? (a.passed ? <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                                          : <XCircle className="w-3.5 h-3.5 text-red-500" />)
                              : <div className="w-3.5 h-3.5 rounded-full border border-gray-300" />}
                            <span className="font-mono text-gray-600">{a.type}{a.value ? `=${a.value}` : ''}</span>
                            {res && <span className="text-gray-400">· {a.detail}</span>}
                          </div>
                        ))}
                      </div>
                      {res?.signatureDetail && (
                        <p className={`text-xs ${res.signed ? 'text-green-700' : 'text-red-600'}`}>
                          {res.signatureDetail}
                        </p>
                      )}
                      {res && res.actualErrorCodes?.length > 0 && (
                        <p className="text-xs text-gray-400">
                          engine returned: <span className="font-mono">{res.actualErrorCodes.join(', ')}</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
